import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Calendar, FolderOpen, Tag, AlertCircle, MessageSquare, Paperclip, Send, Upload, X, FileText, Download, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });
};

// Check if issue is in a closed/resolved state
const isIssueClosed = (status) => {
  if (!status?.name) return false;
  const closedStates = ['resolved', 'closed', 'chiuso', 'risolto'];
  return closedStates.some(s => status.name.toLowerCase().includes(s));
};

// Get file icon based on content type
const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) {
    return <ImageIcon className="h-4 w-4 text-green-500" />;
  }
  return <FileText className="h-4 w-4 text-[rgb(59,130,246)]" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Markdown renderer component
const MarkdownContent = ({ content }) => {
  if (!content) return null;
  
  return (
    <div className="prose prose-invert prose-sm max-w-none">
      <ReactMarkdown
        components={{
          p: ({ children }) => <p className="text-[rgb(250,250,250)] font-mono mb-2 whitespace-pre-wrap">{children}</p>,
          strong: ({ children }) => <strong className="text-[rgb(250,250,250)] font-bold">{children}</strong>,
          em: ({ children }) => <em className="text-[rgb(200,200,210)]">{children}</em>,
          code: ({ children }) => <code className="bg-[rgb(63,63,70)] px-1 py-0.5 rounded text-[rgb(129,140,248)]">{children}</code>,
          pre: ({ children }) => <pre className="bg-[rgb(24,24,27)] p-3 rounded overflow-x-auto">{children}</pre>,
          ul: ({ children }) => <ul className="list-disc list-inside text-[rgb(250,250,250)] font-mono">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside text-[rgb(250,250,250)] font-mono">{children}</ol>,
          li: ({ children }) => <li className="text-[rgb(250,250,250)] font-mono">{children}</li>,
          a: ({ href, children }) => <a href={href} className="text-[rgb(59,130,246)] hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
          h1: ({ children }) => <h1 className="text-xl font-bold text-[rgb(250,250,250)] font-mono mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-lg font-bold text-[rgb(250,250,250)] font-mono mt-3 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-base font-bold text-[rgb(250,250,250)] font-mono mt-2 mb-1">{children}</h3>,
          blockquote: ({ children }) => <blockquote className="border-l-4 border-[rgb(59,130,246)] pl-4 text-[rgb(161,161,170)] italic">{children}</blockquote>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

function CustomerIssueDetail() {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const [issue, setIssue] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // New note form
  const [newNote, setNewNote] = useState('');
  const [noteAttachments, setNoteAttachments] = useState([]);
  const [submittingNote, setSubmittingNote] = useState(false);

  useEffect(() => {
    fetchIssueDetail();
  }, [issueId]);

  const fetchIssueDetail = async () => {
    setLoading(true);
    setError('');

    try {
      const sessionToken = localStorage.getItem('customer_session');
      
      const issueResponse = await axios.get(`${BACKEND_URL}/api/customer/issue/${issueId}`, {
        params: { session_token: sessionToken }
      });

      setIssue(issueResponse.data.issue);
      
      // Filter out hidden notes
      const publicNotes = (issueResponse.data.notes || []).filter(
        note => note.view_state?.name === 'public' || !note.view_state
      );
      setNotes(publicNotes);

    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/customer');
      } else {
        setError(err.response?.data?.detail || 'Errore nel caricamento del dettaglio');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNoteAttachments([...noteAttachments, ...files]);
  };

  const removeAttachment = (index) => {
    setNoteAttachments(noteAttachments.filter((_, i) => i !== index));
  };

  const handleSubmitNote = async () => {
    if (!newNote.trim() && noteAttachments.length === 0) {
      toast.error('Inserisci un testo o un allegato');
      return;
    }

    setSubmittingNote(true);

    try {
      const sessionToken = localStorage.getItem('customer_session');
      
      // Convert attachments to base64
      const filesBase64 = [];
      for (const file of noteAttachments) {
        try {
          const base64Content = await fileToBase64(file);
          filesBase64.push({
            name: file.name,
            content: base64Content
          });
        } catch (err) {
          console.error('Error converting file:', err);
        }
      }

      await axios.post(`${BACKEND_URL}/api/customer/issue/${issueId}/note`, {
        session_token: sessionToken,
        text: newNote,
        files: filesBase64
      });

      toast.success('Nota aggiunta con successo');
      setNewNote('');
      setNoteAttachments([]);
      fetchIssueDetail(); // Refresh notes

    } catch (err) {
      let errorMsg = 'Errore nell\'invio della nota';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        errorMsg = typeof detail === 'string' ? detail : JSON.stringify(detail);
      }
      toast.error(errorMsg);
    } finally {
      setSubmittingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[rgb(9,9,11)] flex items-center justify-center">
        <div className="text-[rgb(161,161,170)] font-mono">Caricamento...</div>
      </div>
    );
  }

  if (error || !issue) {
    return (
      <div className="min-h-screen bg-[rgb(9,9,11)] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-500 font-mono">{error || 'Issue non trovata'}</p>
          <Button
            onClick={() => navigate('/customer')}
            className="mt-4 bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono"
          >
            Torna alla Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const isClosed = isIssueClosed(issue.status);

  return (
    <div className="min-h-screen bg-[rgb(9,9,11)]">
      {/* Header */}
      <div className="bg-[rgb(24,24,27)] border-b border-[rgb(39,39,42)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              onClick={() => navigate('/customer')}
              variant="ghost"
              className="text-[rgb(161,161,170)] hover:text-white font-mono"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna alla Dashboard
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Issue Header */}
        <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl font-bold font-mono text-[rgb(59,130,246)]">
                  #{issue.id}
                </span>
                <span className={`px-3 py-1 rounded text-sm font-mono ${
                  isClosed
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {issue.status?.name || 'N/A'}
                </span>
              </div>
              <h1 className="text-2xl font-bold font-mono text-[rgb(250,250,250)] mb-2">
                {issue.summary}
              </h1>
            </div>
          </div>

          {/* Meta Information - No handler/assignee shown */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-[rgb(161,161,170)]" />
              <div>
                <div className="text-xs text-[rgb(113,113,122)] font-mono">Progetto</div>
                <div className="text-sm text-[rgb(250,250,250)] font-mono">{issue.project?.name || 'N/A'}</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-[rgb(161,161,170)]" />
              <div>
                <div className="text-xs text-[rgb(113,113,122)] font-mono">Categoria</div>
                <div className="text-sm text-[rgb(250,250,250)] font-mono">{issue.category?.name || 'N/A'}</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[rgb(161,161,170)]" />
              <div>
                <div className="text-xs text-[rgb(113,113,122)] font-mono">Creata il</div>
                <div className="text-sm text-[rgb(250,250,250)] font-mono">
                  {new Date(issue.created_at).toLocaleDateString('it-IT')}
                </div>
              </div>
            </div>

            {issue.due_date && (
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[rgb(249,115,22)]" />
                <div>
                  <div className="text-xs text-[rgb(113,113,122)] font-mono">Scadenza</div>
                  <div className="text-sm text-[rgb(249,115,22)] font-mono">
                    {new Date(issue.due_date).toLocaleDateString('it-IT')}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Description with Markdown */}
          <div>
            <h3 className="text-sm font-mono text-[rgb(161,161,170)] mb-2">Descrizione</h3>
            <div className="bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded p-4">
              <MarkdownContent content={issue.description || 'Nessuna descrizione disponibile'} />
            </div>
          </div>

          {/* Additional Info with Markdown */}
          {issue.additional_information && (
            <div className="mt-4">
              <h3 className="text-sm font-mono text-[rgb(161,161,170)] mb-2">Informazioni Aggiuntive</h3>
              <div className="bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded p-4">
                <MarkdownContent content={issue.additional_information} />
              </div>
            </div>
          )}

          {/* Attachments */}
          {issue.attachments && issue.attachments.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-mono text-[rgb(161,161,170)] mb-2">
                <Paperclip className="h-4 w-4 inline mr-1" />
                Allegati ({issue.attachments.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {issue.attachments.map((att, idx) => {
                  const sessionToken = localStorage.getItem('customer_session');
                  
                  const handleDownload = async (e) => {
                    e.preventDefault();
                    try {
                      const response = await fetch(
                        `${BACKEND_URL}/api/customer/issue/${issueId}/attachments/${att.id}?session_token=${sessionToken}`
                      );
                      
                      if (!response.ok) {
                        throw new Error('Download failed');
                      }
                      
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = att.filename || `attachment_${att.id}`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error('Download error:', error);
                      toast.error('Errore nel download del file');
                    }
                  };
                  
                  return (
                    <button
                      key={idx}
                      onClick={handleDownload}
                      className="flex items-center gap-2 px-3 py-2 bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded hover:bg-[rgb(63,63,70)] transition-colors group cursor-pointer"
                    >
                      {getFileIcon(att.content_type)}
                      <span className="text-sm font-mono text-[rgb(250,250,250)]">{att.filename}</span>
                      <span className="text-xs text-[rgb(113,113,122)]">({formatFileSize(att.size)})</span>
                      <Download className="h-4 w-4 text-[rgb(161,161,170)] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Notes Section */}
        <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="h-5 w-5 text-[rgb(161,161,170)]" />
            <h2 className="text-xl font-bold font-mono text-[rgb(250,250,250)]">
              Note ({notes.length})
            </h2>
          </div>

          {/* Add Note Form - only if issue is not closed */}
          {!isClosed && (
            <div className="mb-6 p-4 bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded-lg">
              <h3 className="text-sm font-mono text-[rgb(161,161,170)] mb-3">Aggiungi una nota</h3>
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Scrivi una nota... (supporta Markdown)"
                rows={4}
                className="bg-[rgb(24,24,27)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono mb-3"
              />
              
              {/* Attachments */}
              <div className="flex flex-wrap gap-2 mb-3">
                {noteAttachments.map((file, index) => (
                  <div key={index} className="flex items-center gap-2 px-3 py-1 bg-[rgb(24,24,27)] border border-[rgb(63,63,70)] rounded">
                    <FileText className="h-4 w-4 text-[rgb(59,130,246)]" />
                    <span className="text-sm font-mono text-[rgb(250,250,250)]">{file.name}</span>
                    <button
                      onClick={() => removeAttachment(index)}
                      className="text-red-500 hover:text-red-400"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center justify-between">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 text-[rgb(161,161,170)] hover:text-white transition-colors">
                    <Upload className="h-4 w-4" />
                    <span className="text-sm font-mono">Aggiungi allegato</span>
                  </div>
                </label>
                
                <Button
                  onClick={handleSubmitNote}
                  disabled={submittingNote || (!newNote.trim() && noteAttachments.length === 0)}
                  className="bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono"
                >
                  {submittingNote ? (
                    'Invio...'
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Invia
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {isClosed && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-green-400 font-mono text-sm">
                Questa segnalazione è stata risolta. Non è possibile aggiungere nuove note.
              </p>
            </div>
          )}

          {/* Notes List */}
          {notes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[rgb(161,161,170)] font-mono">Nessuna nota disponibile</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notes.map((note, index) => (
                <div
                  key={note.id || index}
                  className="bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-mono text-[rgb(161,161,170)]">
                      {note.reporter?.real_name || note.reporter?.name || 'Utente'}
                    </span>
                    <span className="text-xs text-[rgb(113,113,122)] font-mono">
                      {new Date(note.created_at).toLocaleString('it-IT')}
                    </span>
                  </div>
                  <MarkdownContent content={note.text} />
                  
                  {/* Note attachments */}
                  {note.attachments && note.attachments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[rgb(63,63,70)]">
                      <div className="flex flex-wrap gap-2">
                        {note.attachments.map((att, idx) => {
                          const handleNoteAttachmentDownload = async (e) => {
                            e.preventDefault();
                            const sessionToken = localStorage.getItem('customer_session');
                            try {
                              const response = await fetch(
                                `${BACKEND_URL}/api/customer/issue/${issueId}/attachments/${att.id}?session_token=${sessionToken}`
                              );
                              
                              if (!response.ok) {
                                throw new Error('Download failed');
                              }
                              
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = att.filename || `attachment_${att.id}`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error('Download error:', error);
                              toast.error('Errore nel download del file');
                            }
                          };
                          
                          return (
                            <button
                              key={idx}
                              onClick={handleNoteAttachmentDownload}
                              className="flex items-center gap-1 px-2 py-1 bg-[rgb(24,24,27)] border border-[rgb(63,63,70)] rounded text-xs hover:bg-[rgb(63,63,70)] cursor-pointer"
                            >
                              <Paperclip className="h-3 w-3 text-[rgb(59,130,246)]" />
                              <span className="font-mono text-[rgb(250,250,250)]">{att.filename}</span>
                              <Download className="h-3 w-3 text-[rgb(161,161,170)]" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CustomerIssueDetail;
